import { DecoratedFormProps, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldInputMask } from 'src/components/redux-form/fields/FieldInputMask';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeMedicalRelease } from '../../../../../store/actions/intakeSent-actions';
import { MedicalReleaseOfInformationFormData } from './MedicalReleaseOfInformationForm';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

interface MedicalReleaseOfInformationThirdFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<MedicalReleaseOfInformationFormData, MedicalReleaseOfInformationThirdFormProps>;

const validators: Validators<MedicalReleaseOfInformationFormData> = {
    patientName: [required({ message: 'Parent Name is required.' }), maxLength({ max: 255 })],
    dob: [required({ message: 'Date of Birth is required.' }), maxLength({ max: 255 })],

    patientAddress: [required({ message: 'Parent Address is required.' }), maxLength({ max: 255 })],
    telephone: [maxLength({ max: 255 }), minPhoneLength({ min: 14 })],

    providerName: [
        required({ message: 'Name of Provider, Institution and/or Agency is required.' }),
        maxLength({ max: 255 }),
    ],
    providerAddress: [required({ message: 'Provider Address is required.' }), maxLength({ max: 255 })],

    providerPhone: [required({ message: 'Provider Phone is required.' }), minPhoneLength({ min: 14 })],
    providerEmail: [emailList(), maxLength({ max: 255 })],

    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    ssnFour: [required({ message: 'Last 4 Digits of the SSN is required.' }), minLength({ min: 4 })],

    relToPatient: [required({ message: 'Your Relationship to Patient is required.' }), maxLength({ max: 255 })],
    dateCreate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const MedicalReleaseOfInformationThirdForm: React.FunctionComponent<
    InjectedProps & MedicalReleaseOfInformationThirdFormProps
> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & MedicalReleaseOfInformationThirdFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Medical Release of Information</h2>
            <div>
                Please submit a separate form for each provider. (Ex: Child's school/pediatrician)
            </div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={9}
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="patientName"
                type="text"
                validate={validators.patientName}
                wrapperConfig={{
                    label: 'Patient Name *',
                }}
            />
            <FieldDate
                name="dob"
                type="text"
                date={true}
                validate={validators.dob}
                wrapperConfig={{
                    label: 'Date of Birth *',
                }}
            />
            <FieldInput
                name="patientAddress"
                type="text"
                validate={validators.patientAddress}
                wrapperConfig={{
                    label: 'Patient Address *',
                }}
            />
            <FieldInputMask
                mask={'+1 999-999-9999'}
                name="telephone"
                type="phone"
                validate={validators.telephone}
                wrapperConfig={{
                    label: 'Telephone',
                }}
            />

            <div className={classNames(styles.textBox)}>
                I hereby authorize Manhattan Psychology Group, PC to disclose and/or obtain the following information in
                writing and/or verbally for the purpose of educational, psychiatric and/or medical health planning:
            </div>

            <FieldInput
                name="isAny"
                type="hidden"
                validate={validators.isAny}
                wrapperConfig={{
                    label: '',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />

            <FieldInput
                name="isPsychoInfo"
                type="checkbox"
                validate={validators.isPsychoInfo}
                wrapperConfig={{
                    label: 'Psycho/Neuro/Social Evaluations',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />
            <FieldInput
                name="isMedicalHistory"
                type="checkbox"
                validate={validators.isMedicalHistory}
                wrapperConfig={{
                    label: 'Medical History',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />
            <FieldInput
                name="isProgressReport"
                type="checkbox"
                validate={validators.isProgressReport}
                wrapperConfig={{
                    label: 'Updates/Progress Reports',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />
            <FieldInput
                name="isPsychiatric"
                type="checkbox"
                validate={validators.isPsychiatric}
                wrapperConfig={{
                    label: 'Psychiatric/Emergency evaluations',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />
            <FieldInput
                name="isEducational"
                type="checkbox"
                validate={validators.isEducational}
                wrapperConfig={{
                    label: 'Educational / School Reports',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />
            <FieldInput
                name="isOther"
                type="checkbox"
                validate={validators.isOther}
                wrapperConfig={{
                    label: 'Other',
                    classes: {
                        inputWrapper: 'customCheckbox ' + classNames(styles.fixCheckbox),
                    },
                }}
            />

            <FieldInput
                name="providerName"
                type="text"
                validate={validators.providerName}
                wrapperConfig={{
                    label: 'Name of Provider, Institution and/or Agency *',
                }}
            />

            <FieldInput
                name="providerAddress"
                type="text"
                validate={validators.providerAddress}
                wrapperConfig={{
                    label: 'Provider Address *',
                }}
            />

            <FieldInputMask
                mask={'+1 999-999-9999'}
                name="providerPhone"
                type="phone"
                validate={validators.providerPhone}
                wrapperConfig={{
                    label: 'Provider Phone *',
                }}
            />

            <FieldInput
                name="providerEmail"
                type="text"
                validate={validators.providerEmail}
                wrapperConfig={{
                    label: 'Provider Email',
                }}
            />

            <div className={classNames(styles.textBox)}>
                By entering my full name and the last 4 digits of my SSN, I/we consent to the release and exchange of
                the above information with the specified provider and Manhattan Psychology Group, PC. I/we understand
                that this consent may be revoked at any time in writing and will expire one year from the date signed
                below. I understand that the above information after it has been disclosed will be kept confidential and
                is not authorized for further disclosure to third parties.
            </div>

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Full Name *',
                }}
            />

            <FieldInput
                name="ssnFour"
                type="text"
                validate={validators.ssnFour}
                wrapperConfig={{
                    label: 'Last 4 Digits of the SSN *',
                }}
            />

            <FieldInput
                name="relToPatient"
                type="text"
                validate={validators.relToPatient}
                wrapperConfig={{
                    label: 'Your Relationship to Patient *',
                }}
            />

            <FieldDate
                name="dateCreate"
                type="text"
                date={true}
                validate={validators.dateCreate}
                wrapperConfig={{
                    label: 'Date *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<MedicalReleaseOfInformationFormData, MedicalReleaseOfInformationThirdFormProps>({
    form: 'medical-release-of-information-third-form',
    enableReinitialize: true,
    validate: (values: MedicalReleaseOfInformationFormData): FormErrors<MedicalReleaseOfInformationFormData> => {
        const errors: FormErrors<MedicalReleaseOfInformationFormData> = {};

        if (
            !values.isPsychiatric &&
            !values.isEducational &&
            !values.isOther &&
            !values.isPsychoInfo &&
            !values.isMedicalHistory &&
            !values.isProgressReport
        ) {
            errors.isAny = 'Check at least one option';
        }

        return errors;
    },
    onSubmit: async (
        data: MedicalReleaseOfInformationFormData,
        dispatch: Dispatch<any>,
        props: MedicalReleaseOfInformationThirdFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeMedicalRelease({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<
            MedicalReleaseOfInformationFormData,
            MedicalReleaseOfInformationThirdFormProps,
            string
        >,
    ) {
        //toast.success(`The form was successfully sent.`);
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {

        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(MedicalReleaseOfInformationThirdForm);
